import { usePublicHostname } from '../../seo/PublicBootstrap.jsx';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PublicHeader } from '@mts/ui';
import { usePublicTenant } from '../../hooks/usePublicTenant';
import { buildPublicHeaderConfig } from '../../config/publicShellConfig';
import { authClient } from '../../lib/authClient';
import { resetPostHogIdentity } from '../../lib/posthog';
import { getApiBaseUrl } from '../../services/api';
import { fetchOwnProfile } from '../../services/userProfiles';
import TenantWordmark from '../brand/TenantWordmark';

function Header({ isMobile = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const hostname = usePublicHostname();
  const { tenant, spotlightName, contactEmail, contactPhone, tenantLogoConfig, tenantLogoImageUrl } = usePublicTenant();
  const { data: session, isPending } = authClient.useSession();
  const [profileUserId, setProfileUserId] = useState('');
  const isAuthenticated = Boolean(session?.user?.id || profileUserId);

  useEffect(() => {
    if (isPending || session?.user?.id) {
      setProfileUserId('');
      return undefined;
    }

    let ignore = false;
    fetchOwnProfile({ optional: true })
      .then((payload) => {
        if (!ignore) {
          setProfileUserId(String(payload?.profile?.id || ''));
        }
      })
      .catch(() => {
        if (!ignore) {
          setProfileUserId('');
        }
      });

    return () => {
      ignore = true;
    };
  }, [isPending, session?.user?.id]);

  const handleLogout = async () => {
    try {
      await authClient.signOut();
    } catch (_error) {
      // Best effort sign-out for UX continuity.
    }
    try {
      await fetch(`${getApiBaseUrl()}/api/auth/portal-token/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (_error) {
      // Best effort sign-out for portal-token sessions.
    }
    resetPostHogIdentity();
    navigate('/', { replace: true });
  };

  const utilityLinks = isAuthenticated
    ? [
        { label: 'Spotlight Blog', href: '/blog' },
        { label: 'Local Experts', href: '/experts' },
        { label: 'Your Account', href: '/profile' },
        { label: 'Logout', href: '#', onClick: handleLogout },
      ]
    : [
        { label: 'Spotlight Blog', href: '/blog' },
        { label: 'Local Experts', href: '/experts' },
        { label: 'Login', href: '/login' },
        { label: 'Register Now', href: '/register' },
      ];

  const mobileUserAuth = isAuthenticated
    ? { label: 'Account', href: '/profile' }
    : { label: 'Login', href: '/login' };

  const resolvedHeaderLogo = {
    alt: `${spotlightName} logo`,
    ...(tenantLogoImageUrl
      ? { src: tenantLogoImageUrl }
      : { element: <TenantWordmark cityName={tenantLogoConfig?.cityName || spotlightName} /> }),
  };

  const headerConfig = buildPublicHeaderConfig({
    pathname: location.pathname,
    hostname,
    spotlightName,
    isMobile,
    logo: resolvedHeaderLogo,
    contactEmail,
    contactPhone,
    utilityLinks,
    navigationItems: tenant?.websiteNavigationItems || [],
  });

  const handleNavigate = (href) => {
    if (!href || href === '#') {
      return;
    }

    if (href.startsWith('/')) {
      navigate(href);
      return;
    }

    window.location.assign(href);
  };

  return <PublicHeader {...headerConfig} sticky onNavigate={handleNavigate} mobileUserAuth={mobileUserAuth} />;
}

export default Header;
